import { useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertOctagon, Euro, Mail, Loader2, Building2, Phone, Send, CheckCircle2, Hash } from "lucide-react";
import api from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";

const STATUS_LABELS = {
  pending:  { label: "Offen",        color: "bg-amber-500/15 text-amber-300 border-amber-500/40" },
  invoiced: { label: "Mahnung gesendet", color: "bg-sky-500/15 text-sky-300 border-sky-500/40" },
  paid:     { label: "Bezahlt",      color: "bg-[#00E575]/15 text-[#00E575] border-[#00E575]/40" },
  waived:   { label: "Erlassen",     color: "bg-zinc-700/30 text-zinc-300 border-zinc-600" },
};

const _eur = (v) =>
  (typeof v === "number" ? v : Number(v || 0)).toLocaleString("de-DE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  });

export default function AdminPenalties() {
  const [filter, setFilter] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/penalties?status=${filter}`);
      setItems(data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Konnte Pönalen nicht laden");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [filter]);

  const sendWarning = async (id, installerName) => {
    if (!window.confirm(`Mahnung an ${installerName} per E-Mail versenden?\n\nEs wird ein Pönale-PDF generiert und angehängt. Der Status wechselt auf „Mahnung gesendet".`)) return;
    setActingId(id);
    try {
      const { data } = await api.post(`/admin/penalties/${id}/send`);
      toast.success(`Mahnung versendet · Zahlungsziel ${data?.due_date || "—"}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "E-Mail-Versand fehlgeschlagen");
    } finally {
      setActingId(null);
    }
  };

  const totalPending = items
    .filter((p) => p.status === "pending")
    .reduce((sum, p) => sum + Number(p.amount_eur || 0), 0);

  return (
    <div className="min-h-screen bg-[#09090B]" data-testid="admin-penalties-page">
      <AppHeader title="Pönalen" back="/dashboard" />
      <main className="max-w-5xl mx-auto px-3 sm:px-5 py-5 space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <AlertOctagon className="text-amber-300" size={22} />
            <div>
              <h1 className="font-display text-xl sm:text-2xl font-bold">Auktion-Pönalen</h1>
              <p className="text-[12px] text-zinc-300 font-medium">Mahnungen an Installateure bei abgelehntem Auktions-Zuschlag (5 %)</p>
            </div>
          </div>
          <div className="flex rounded-xl bg-[#18181B] border border-zinc-800 overflow-hidden">
            {["pending", "invoiced", "paid", "waived", "all"].map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                data-testid={`penalties-filter-${s}`}
                className={`px-3 h-9 text-[12px] font-bold uppercase tracking-wider ${filter === s ? "bg-[#00E575] text-[#09090B]" : "text-zinc-400 hover:bg-zinc-800"}`}
              >
                {STATUS_LABELS[s]?.label || "Alle"}
              </button>
            ))}
          </div>
        </div>

        {filter === "pending" && items.length > 0 && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4 flex items-center justify-between" data-testid="penalties-pending-summary">
            <div className="flex items-center gap-2 text-amber-200">
              <Euro size={16} />
              <span className="text-[13px] font-semibold">{items.length} offene Pönale{items.length === 1 ? "" : "n"}</span>
            </div>
            <div className="text-amber-100 font-display text-lg font-bold">{_eur(totalPending)}</div>
          </div>
        )}

        {loading && (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-8 text-center text-zinc-400 inline-flex w-full justify-center gap-2">
            <Loader2 size={16} className="animate-spin" /> lädt…
          </div>
        )}

        {!loading && items.length === 0 && (
          <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-8 text-center text-zinc-300 text-sm font-medium" data-testid="penalties-empty">
            Keine Pönalen mit Status „{STATUS_LABELS[filter]?.label || "Alle"}&quot;.
          </div>
        )}

        {!loading && items.map((p) => {
          const inst = p.installer || {};
          const auc = p.auction || {};
          const meta = STATUS_LABELS[p.status] || STATUS_LABELS.pending;
          const fullName = `${inst.first_name || ""} ${inst.last_name || ""}`.trim() || inst.email || "—";
          return (
            <article key={p.id} className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5" data-testid={`penalty-card-${p.id}`}>
              <header className="flex flex-wrap items-start justify-between gap-3 mb-3 pb-3 border-b border-zinc-800">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h2 className="font-display text-lg font-bold">{fullName}</h2>
                    <span className={`text-[12px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md border ${meta.color}`}>{meta.label}</span>
                  </div>
                  {inst.company_name && (
                    <div className="flex items-center gap-1.5 text-[12px] text-zinc-300 font-medium"><Building2 size={12} /> {inst.company_name}</div>
                  )}
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-[11.5px] text-zinc-400">
                    {inst.email && <a href={`mailto:${inst.email}`} className="inline-flex items-center gap-1 hover:text-zinc-200"><Mail size={11} /> {inst.email}</a>}
                    {inst.phone && <a href={`tel:${inst.phone}`} className="inline-flex items-center gap-1 hover:text-zinc-200"><Phone size={11} /> {inst.phone}</a>}
                    <span className="inline-flex items-center gap-1"><Hash size={11} /> Auktion {String(p.auction_id || "").slice(0, 8)}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold font-bold">Pönale (5 %)</div>
                  <div className="font-display text-2xl font-bold text-amber-200">{_eur(p.amount_eur)}</div>
                </div>
              </header>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-[12.5px]">
                <Detail label="Gebot" value={auc.winning_bid_eur ? _eur(auc.winning_bid_eur) : "—"} />
                <Detail label="Ablehnungsgrund" value={p.reason || "—"} />
                <Detail label="Eingangsdatum" value={p.created_at ? new Date(p.created_at).toLocaleString("de-DE") : "—"} />
                {p.sent_at && <Detail label="Versendet am" value={new Date(p.sent_at).toLocaleString("de-DE")} />}
                {p.due_date && <Detail label="Zahlungsziel" value={p.due_date} />}
                {p.paid_at && <Detail label="Bezahlt am" value={new Date(p.paid_at).toLocaleString("de-DE")} />}
              </div>

              {p.status === "pending" && (
                <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
                  <button
                    onClick={() => sendWarning(p.id, fullName)}
                    disabled={actingId === p.id || !inst.email}
                    title={inst.email ? "" : "Installateur hat keine E-Mail-Adresse"}
                    data-testid={`penalty-send-${p.id}`}
                    className="h-10 px-4 rounded-lg bg-amber-500 text-[#09090B] font-bold text-[12.5px] inline-flex items-center gap-1.5 hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {actingId === p.id ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                    Mahnung per E-Mail senden
                  </button>
                </div>
              )}

              {p.status === "invoiced" && (
                <div className="mt-3 bg-sky-500/10 border border-sky-500/30 rounded-lg p-3 inline-flex items-center gap-2 text-sky-200 text-[12px] font-medium">
                  <CheckCircle2 size={14} /> Mahnung versendet — wartet auf Zahlungseingang
                </div>
              )}
            </article>
          );
        })}
      </main>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <div className="text-[12px] uppercase tracking-widest text-zinc-300 font-semibold font-bold">{label}</div>
      <div className="text-zinc-100 break-words">{value || "—"}</div>
    </div>
  );
}
